import {
  FlexBox,
  Grid,
  LanguageSwitch,
  Menu,
  space,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import { useTranslation } from "react-i18next";
import styledComponents from "styled-components";

import { ReactComponent as GermanFlag } from "../../public/flags/de.svg";
import { ReactComponent as SpanishFlag } from "../../public/flags/es.svg";
import { ReactComponent as FrenchFlag } from "../../public/flags/fr.svg";
import { ReactComponent as EnglishFlag } from "../../public/flags/gb.svg";
import { ReactComponent as ChatIcon } from "../../public/material-icons/chat-bubble.svg";
import { ReactComponent as ChatOutlineIcon } from "../../public/material-icons/chat-bubble-outline.svg";
import { ReactComponent as DashboardfilledIcon } from "../../public/material-icons/dashboard.svg";
import { ReactComponent as DashboardIcon } from "../../public/material-icons/dashboard-empty.svg";
import { ReactComponent as ErrorIcon } from "../../public/material-icons/error.svg";
import { ReactComponent as ErrorOutlineIcon } from "../../public/material-icons/error-outline.svg";
import { ReactComponent as LogoutIcon } from "../../public/material-icons/logout.svg";
import { ReactComponent as TimeIcon } from "../../public/material-icons/schedule.svg";
import { ReactComponent as TimeFilledIcon } from "../../public/material-icons/schedule-filled.svg";
import { ReactComponent as TableChartFilledIcon } from "../../public/material-icons/table-chart.svg";
import { ReactComponent as TableChartIcon } from "../../public/material-icons/table-chart-empty.svg";
import { useAuthContext, useCurrentUser } from "../auth-provider";
import { useActiveRoom } from "../hooks/use-rooms";

type MenuContentProps = {
  toggleHeader?: () => void;
};

const languages = [
  {
    value: "de",
    icon: <GermanFlag />,
  },
  {
    value: "en",
    icon: <EnglishFlag />,
  },
  {
    value: "fr",
    icon: <FrenchFlag />,
  },
  {
    value: "es",
    icon: <SpanishFlag />,
  },
];

const StyledVersionContainer = styledComponents.div`
  position: absolute;
  right: ${space(10)};
  bottom: ${space(10)};
`;

const StyledLogoutButton = styledComponents.button`
  display: flex;
  align-items: center;
  gap: ${space(2)};
  background: transparent;
  border: 1px solid white;
  color: white;
  padding: ${space(2)} ${space(4)};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  svg {
    width: 20px;
    height: 20px;
    fill: white;
  }
`;

export const MenuContent = ({ toggleHeader }: MenuContentProps) => {
  const { activeRoom } = useActiveRoom();
  const { i18n, t } = useTranslation();
  const user = useCurrentUser();
  const { logout } = useAuthContext();

  const onLanguageSwitch = (lang: string) => {
    i18n.changeLanguage(lang);
    if (toggleHeader) {
      toggleHeader();
    }
  };

  const handleLogout = () => {
    if (toggleHeader) {
      toggleHeader();
    }
    logout();
  };

  return (
    <Grid columnGap={space(20)} rowGap={space(20)}>
      <Grid.Column width={4}>
        <Menu.Column headline={t("page.analytics")}>
          <Menu.Link onClick={toggleHeader} to={"/" + location.search}>
            {({ isActive }) =>
              isActive ? (
                <>
                  <DashboardfilledIcon /> {t("page.dashboard")}
                </>
              ) : (
                <>
                  <DashboardIcon /> {t("page.dashboard")}
                </>
              )
            }
          </Menu.Link>
        </Menu.Column>
      </Grid.Column>
      <Grid.Column width={4}>
        <Menu.Column headline={t("page.control")}>
          <Menu.Link onClick={toggleHeader} to={"/rooms" + location.search}>
            {({ isActive }) =>
              isActive ? (
                <>
                  <TimeFilledIcon /> {t("page.rooms")}
                </>
              ) : (
                <>
                  <TimeIcon /> {t("page.rooms")}
                </>
              )
            }
          </Menu.Link>
          <Menu.Link
            onClick={toggleHeader}
            to={"/workplaces" + location.search}
          >
            {({ isActive }) =>
              isActive ? (
                <>
                  <TableChartFilledIcon /> {t("page.workplaces")}
                </>
              ) : (
                <>
                  <TableChartIcon /> {t("page.workplaces")}
                </>
              )
            }
          </Menu.Link>
          <Menu.Link onClick={toggleHeader} to={"/time" + location.search}>
            {({ isActive }) =>
              isActive ? (
                <>
                  <TimeFilledIcon /> {t("page.timecontrol")}
                </>
              ) : (
                <>
                  <TimeIcon /> {t("page.timecontrol")}
                </>
              )
            }
          </Menu.Link>
        </Menu.Column>
      </Grid.Column>
      <Grid.Column width={4}>
        <Menu.Column headline={t("page.support")}>
          <Menu.Link
            onClick={toggleHeader}
            to={"/error-history" + location.search}
          >
            {({ isActive }) =>
              isActive ? (
                <>
                  <ErrorIcon /> {t("page.errorhistory")}
                </>
              ) : (
                <>
                  <ErrorOutlineIcon /> {t("page.errorhistory")}
                </>
              )
            }
          </Menu.Link>
          <Menu.Link onClick={toggleHeader} to={"/contact" + location.search}>
            {({ isActive }) =>
              isActive ? (
                <>
                  <ChatIcon /> {t("page.contact")}
                </>
              ) : (
                <>
                  <ChatOutlineIcon /> {t("page.contact")}
                </>
              )
            }
          </Menu.Link>
        </Menu.Column>
      </Grid.Column>

      <Grid.Column width={4}>
        <LanguageSwitch
          languages={languages}
          onClick={onLanguageSwitch}
          selectedLanguage={i18n.language}
          title={t("menu.languages")}
        />
      </Grid.Column>
      <Grid.Column width={4}>
        <FlexBox flexDirection="column" gap={space(6)}>
          <Typography color="white" size="lg">
            {t("header.contactperson")}
          </Typography>
          <Typography color="white" weight="semiBold">
            {activeRoom?.contactPerson}
          </Typography>
          <Typography color="white" whiteSpace="pre-line">
            {activeRoom?.contactData}
          </Typography>
        </FlexBox>
      </Grid.Column>
      <Grid.Column width={4}>
        <FlexBox flexDirection="column" gap={space(6)}>
          <Typography color="white" size="lg">
            {t("menu.user")}
          </Typography>
          <Typography color="white" weight="semiBold">
            {user?.name || user?.email || "-"}
          </Typography>
          <StyledLogoutButton onClick={handleLogout}>
            <LogoutIcon /> {t("menu.logout")}
          </StyledLogoutButton>
        </FlexBox>
      </Grid.Column>
      <StyledVersionContainer>
        <div style={{ textAlign: "right" }}>
          <Typography block color="white">
            v{import.meta.env.PACKAGE_VERSION}
          </Typography>
        </div>
        <div style={{ textAlign: "right" }}>
          <Typography color="white">
            {import.meta.env.VITE_BRANCH_NAME}
          </Typography>
        </div>
      </StyledVersionContainer>
    </Grid>
  );
};
